import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link2, Plus, CheckCircle2, Settings, Download, Sparkles, Crown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { GET_SUBSCRIPTION_PLAN } from "@/graphql/queries";
import { UpgradePromptDialog } from "@/components/UpgradePromptDialog";
import { ApiErrorScreen, useHasCriticalError } from "@/components/ApiErrorScreen";

const connectors = [
  {
    name: "Frame.io",
    description: "Professional video review and collaboration platform",
    icon: "/frameio.png",
    connected: false,
  },
  {
    name: "Google Drive",
    description: "Import media files directly from your Google Drive",
    icon: "https://www.google.com/drive/static/images/drive/logo-drive.png",
    connected: false,
  },
  {
    name: "Dropbox",
    description: "Access and stream files from your Dropbox storage",
    icon: "/dropbox.png",
    connected: false,
  },
];

type Plan = 'PERSONAL' | 'PRO' | 'STUDIO' | 'ENTERPRISE';

const Connectors = () => {
  const navigate = useNavigate();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  
  // Fetch subscription
  const { data: subscriptionData, error: subscriptionError } = useQuery<{ subscription?: { plan?: Plan } | null }>(GET_SUBSCRIPTION_PLAN, {
    errorPolicy: 'all',
  });

  // Check for critical errors
  const queryErrors = [subscriptionError];
  const hasCriticalErrors = useHasCriticalError(queryErrors);

  const currentPlan: Plan = subscriptionData?.subscription?.plan || 'PERSONAL';
  
  // Personal users need to upgrade to Pro for connectors
  // Pro users see "Coming Soon" since feature isn't built yet
  const needsUpgrade = currentPlan === 'PERSONAL';
  const isComingSoon = currentPlan !== 'PERSONAL'; // Pro, Studio, Enterprise

  const handleConnectorClick = () => {
    if (needsUpgrade) {
      setShowUpgradeDialog(true);
    }
    // For Pro+ users, button is disabled (coming soon)
  };

  // Show blocking error screen when critical APIs fail
  if (hasCriticalErrors) {
    return (
      <ApiErrorScreen 
        errors={queryErrors}
        context="Dashboard - Connectors"
      />
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-7xl">
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
            Connectors & Downloads
          </h1>
          <p className="text-muted-foreground mt-2">
            Connect external sources and download software for your streaming sessions
          </p>
        </div>

        {/* Software Downloads */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Software Downloads</h2>
          </div>
          
          <Card
            className="p-6 bg-gradient-to-br from-card to-card/80 border-border transition-all duration-200 hover:border-primary/50 cursor-pointer"
            onClick={() => navigate('/connectors/flow-broadcaster')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl overflow-hidden shadow-md">
                  <img
                    src="/flow-broadcaster-icon.png"
                    alt="Flow Broadcaster"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Flow Broadcaster</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Professional broadcasting software for FlowLabs. Stream directly to your sessions with advanced controls.
                  </p>
                </div>
              </div>
              <Button size="sm" className="gap-2 shrink-0">
                <Download className="w-4 h-4" />
                Download
              </Button>
            </div>
          </Card>
        </div>

        {/* Connected Services */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <h2 className="text-xl font-semibold">Connected Services</h2>
          </div>
          
          <Card className="p-8 bg-muted/30 border-border text-center">
            <div className="space-y-2">
              <Link2 className="w-12 h-12 mx-auto text-muted-foreground" />
              <h3 className="font-semibold text-muted-foreground">No services connected yet</h3>
              <p className="text-sm text-muted-foreground">
                {needsUpgrade 
                  ? "Upgrade to Pro to connect external services"
                  : "Connect your first service below to get started"}
              </p>
            </div>
          </Card>
        </div>

        {/* Available Connectors */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Available Connectors</h2>
            {needsUpgrade && (
              <Badge className="bg-amber-500/10 text-amber-500 border border-amber-500/30 gap-1">
                <Crown className="w-3 h-3" />
                Pro Feature
              </Badge>
            )}
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connectors.map((connector) => (
              <Card 
                key={connector.name}
                className={`p-6 bg-card border-border transition-all duration-200 ${
                  needsUpgrade ? 'hover:border-amber-500/50' : 'hover:border-primary/50'
                }`}
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {connector.icon ? (
                        <img 
                          src={connector.icon} 
                          alt={connector.name}
                          className="w-10 h-10 object-contain"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                          <Link2 className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold">{connector.name}</h3>
                      </div>
                    </div>
                    {needsUpgrade ? (
                      <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-xs">
                        Pro
                      </Badge>
                    ) : isComingSoon ? (
                      <Badge variant="secondary" className="text-xs">
                        Soon
                      </Badge>
                    ) : null}
                  </div>

                  <p className="text-sm text-muted-foreground">
                    {connector.description}
                  </p>

                  {connector.connected ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage
                    </Button>
                  ) : needsUpgrade ? (
                    <Button 
                      variant="outline"
                      size="sm" 
                      className="w-full"
                      onClick={handleConnectorClick}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Upgrade
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      disabled={isComingSoon}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {isComingSoon ? 'Coming Soon' : 'Connect'}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Info Card */}
        <Card className="p-6 bg-muted/30 border-border">
          <div className="space-y-2">
            <h3 className="font-semibold flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              How Connectors Work
            </h3>
            <p className="text-sm text-muted-foreground">
              Connectors allow you to seamlessly integrate media from your favorite cloud storage and streaming platforms. 
              Once connected, you can easily import files and content directly into your FlowLabs sessions.
              {needsUpgrade && (
                <span className="text-amber-500"> Upgrade to Pro to unlock this feature.</span>
              )}
            </p>
          </div>
        </Card>
      </div>

      {/* Upgrade Dialog */}
      <UpgradePromptDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        feature="connectors"
        currentPlan={currentPlan}
        requiredPlan="PRO"
        description="Connectors let you import media directly from Frame.io, Google Drive, and Dropbox into your streaming sessions."
      />
    </div>
  );
};

export default Connectors;
